// src/data/adapters/onlineAdapter.js
import { Global } from "../../helpers/Global";

/** Cabeceras con token */
function tokenHeaders(extra = {}) {
  const t = localStorage.getItem("token");
  return {
    ...extra,
    Authorization: t ? `Bearer ${t}` : "",
    "Content-Type": extra["Content-Type"] || "application/json",
  };
}

/** Normaliza la jornada activa del backend -> shape de la UI */
export function normalizeActiveFromServer(j) {
  if (!j) return null;
  return {
    id: j.id,
    inicio: j.fecha_inicio ?? j.inicio ?? j.created_at ?? null,
    centro_id: j.centro_id ?? j.centro?.id ?? null,
    centro_nombre:
      j.centro ??
      j.centro_nombre ??
      (j.centro && j.centro.nombre) ??
      "",
    latitud_inicio: j.latitud ?? j.latitud_inicio ?? null,
    longitud_inicio: j.longitud ?? j.longitud_inicio ?? null,
  };
}

export const OnlineAdapter = {
  async getCentros() {
    const res = await fetch(
      `${Global.url}centros?permite_jornadas=true`,
      { headers: tokenHeaders(), cache: "no-store" }
    );
    if (!res.ok) throw new Error("No se pudieron obtener los centros (online)");
    return res.json();
  },

  async getActive() {
    const res = await fetch(
      `${Global.url}jornadas/activa?t=${Date.now()}`,
      { headers: tokenHeaders(), cache: "no-store" }
    );
    if (res.status === 204) return null;
    if (!res.ok) throw new Error("Error al leer jornada activa (online)");
    const data = await res.json();
    return normalizeActiveFromServer(data);
  },

  /**
   * Inicia jornada.
   * - Modo normal (online): options.offlineReplay = false (por defecto).
   * - Replay offline: options.offlineReplay = true, options.idemKey = clave estable del outbox.
   *
   * payload típico:
   *   { centro_id, latitud, longitud, fingerprint, inicio, inicio_client }
   */
  async start(payload, options = {}) {
    const {
      centro_id,
      latitud,
      longitud,
      fingerprint,
      inicio,
      inicio_client,
    } = payload || {};

    const offlineReplay = !!options.offlineReplay;
    const idem =
      options.idemKey ||
      (crypto?.randomUUID?.() || `idem_${Date.now()}_${Math.random()}`);

    const headersExtra = {
      "X-Idempotency-Key": idem,
    };
    if (offlineReplay) {
      headersExtra["X-Offline-Replay"] = "1";
    }

    const body = {
      centro_id,
      latitud,
      longitud,
      fingerprint,
    };

    // En modo normal, no pasamos inicio_client al backend (no lo usa).
    // En replay offline, mandamos inicio_client (o, si falta, inicio).
    if (offlineReplay) {
      const ic = inicio_client || inicio;
      if (ic) body.inicio_client = ic;
    } else if (inicio) {
      // Por compatibilidad, podemos seguir mandando 'inicio' aunque el backend no lo use.
      body.inicio = inicio;
    }

    const res = await fetch(`${Global.url}jornadas`, {
      method: "POST",
      headers: tokenHeaders(headersExtra),
      body: JSON.stringify(body),
    });

    if (res.ok) {
      const data = await res.json().catch(() => ({}));
      return { ok: true, conflict: false, status: res.status, data };
    }

    if (res.status === 409) {
      // 409 se trata como “existe activa ya”
      return { ok: true, conflict: true, status: res.status, data: null };
    }

    throw new Error(`Fallo al iniciar (online): ${res.status}`);
  },

  /**
   * Cierra la “jornada actual” del usuario en servidor.
   * - options.offlineReplay = true => manda fin_client + X-Offline-Replay:1
   *
   * payload típico:
   *   {
   *     motivo_fin,
   *     latitud_cierre, longitud_cierre,
   *     en_rango_cierre, distancia_m_cierre,
   *     fin_client   // SOLO en replay offline
   *   }
   */
  async finishActual(payload, options = {}) {
    const {
      motivo_fin = "finalizada",
      latitud_cierre,
      longitud_cierre,
      en_rango_cierre,
      distancia_m_cierre,
      fin_client,
    } = payload || {};

    const offlineReplay = !!options.offlineReplay;
    const headersExtra = {};
    if (offlineReplay) {
      headersExtra["X-Offline-Replay"] = "1";
    }

    const body = {
      motivo_fin,
      latitud_cierre,
      longitud_cierre,
      en_rango_cierre,
      distancia_m_cierre,
    };

    if (offlineReplay && fin_client) {
      body.fin_client = fin_client;
    }

    const res = await fetch(`${Global.url}jornadas/finalizar/actual`, {
      method: "PUT",
      headers: tokenHeaders(headersExtra),
      body: JSON.stringify(body),
    });

    if (res.ok) {
      return { ok: true, idempotent: false, status: res.status };
    }
    // Tratar 404/409 como éxito idempotente (“no había activa”)
    if (res.status === 404 || res.status === 409) {
      return { ok: true, idempotent: true, status: res.status };
    }
    throw new Error(`Fallo al finalizar actual (online): ${res.status}`);
  },

  /** Cancela la “jornada actual” */
  async cancelActual(options = {}) {
    const offlineReplay = !!options.offlineReplay;
    const headersExtra = {};
    if (offlineReplay) {
      headersExtra["X-Offline-Replay"] = "1";
    }

    const res = await fetch(`${Global.url}jornadas/finalizar/actual`, {
      method: "PUT",
      headers: tokenHeaders(headersExtra),
      body: JSON.stringify({ motivo_fin: "cancelada" }),
    });
    if (res.ok) return { ok: true, idempotent: false, status: res.status };
    if (res.status === 404 || res.status === 409) {
      return { ok: true, idempotent: true, status: res.status };
    }
    throw new Error(`Fallo al cancelar actual (online): ${res.status}`);
  },
};
